import { useCallback, useEffect, useState } from 'react'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  type AccountSummaryPaymentMethodItem,
  type AccountSummarySavedCardItem,
} from '@assets/mockdata/PatientTray/mockAccountSummary'
import {
  deletePaymentMethod,
  listPaymentMethods,
  mapPaymentMethodApiToDisplay,
  setDefaultPaymentMethod,
  type SavedCardDisplay,
} from '@services/api/billing/payment-method.api'
import { extractApiError } from '@utils/apiError.util'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useAppSelector } from '@store/hooks'
import {
  isValidPaymentMethodId,
  mapSavedCardsToPaymentMethods,
  mapSavedCardsToSavedCardItems,
} from './patientProfilePaymentMethods.util'

export type UsePatientProfilePaymentMethods = {
  paymentMethods: AccountSummaryPaymentMethodItem[]
  savedCards: AccountSummarySavedCardItem[]
  savedCardDisplays: SavedCardDisplay[]
  isLoading: boolean
  isDeleting: boolean
  isSettingDefault: boolean
  refetch: () => Promise<void>
  deletePaymentMethodById: (id: string) => Promise<boolean>
  setDefaultPaymentMethodById: (id: string) => Promise<boolean>
}

type UsePatientProfilePaymentMethodsOptions = {
  enabled?: boolean
}

const usePatientProfilePaymentMethods = (
  options: UsePatientProfilePaymentMethodsOptions = {},
): UsePatientProfilePaymentMethods => {
  const { enabled = true } = options
  const { showToast } = useNotificationToast()
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)

  const [paymentMethods, setPaymentMethods] = useState<
    AccountSummaryPaymentMethodItem[]
  >([])
  const [savedCards, setSavedCards] = useState<AccountSummarySavedCardItem[]>(
    [],
  )
  const [savedCardDisplays, setSavedCardDisplays] = useState<
    SavedCardDisplay[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSettingDefault, setIsSettingDefault] = useState(false)

  const fetchPaymentMethods = useCallback(async (): Promise<void> => {
    if (!enabled) {
      setPaymentMethods([])
      setSavedCards([])
      setSavedCardDisplays([])
      return
    }

    if (!selectedPatient?.patient_id || !selectedClinic?.clinic_id) {
      setPaymentMethods([])
      setSavedCards([])
      setSavedCardDisplays([])
      return
    }

    setIsLoading(true)
    try {
      const res = await listPaymentMethods(
        selectedPatient.patient_id,
        selectedClinic.clinic_id,
      )
      const methods = res.data?.payment_methods ?? []
      const displays = methods.map(mapPaymentMethodApiToDisplay)
      setSavedCardDisplays(displays)
      setPaymentMethods(mapSavedCardsToPaymentMethods(displays))
      setSavedCards(mapSavedCardsToSavedCardItems(displays))
    } catch {
      setPaymentMethods([])
      setSavedCards([])
      setSavedCardDisplays([])
      showToast(
        createFailedToast(
          'Could not load payment methods',
          'Please try again.',
        ),
      )
    } finally {
      setIsLoading(false)
    }
  }, [
    enabled,
    selectedPatient?.patient_id,
    selectedClinic?.clinic_id,
    showToast,
  ])

  useEffect(() => {
    void fetchPaymentMethods()
  }, [fetchPaymentMethods])

  const deletePaymentMethodById = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id || !isValidPaymentMethodId(id)) {
        showToast(
          createFailedToast(
            'Could not remove payment method',
            'This payment method cannot be removed.',
          ),
        )
        return false
      }

      if (isDeleting) return false

      setIsDeleting(true)
      try {
        await deletePaymentMethod(id)
        await fetchPaymentMethods()
        showToast(
          createSuccessToast(
            'Payment method removed',
            'The payment method has been removed.',
          ),
        )
        return true
      } catch (err: unknown) {
        showToast(
          createFailedToast(
            'Could not remove payment method',
            extractApiError(err, 'Please try again.'),
          ),
        )
        return false
      } finally {
        setIsDeleting(false)
      }
    },
    [fetchPaymentMethods, isDeleting, showToast],
  )

  const setDefaultPaymentMethodById = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id || !isValidPaymentMethodId(id)) {
        showToast(
          createFailedToast(
            'Could not set default payment method',
            'This payment method cannot be set as default.',
          ),
        )
        return false
      }

      const patientId = selectedPatient?.patient_id
      const clinicId = selectedClinic?.clinic_id
      if (!patientId || !clinicId) {
        showToast(
          createFailedToast(
            'Could not set default payment method',
            'Patient or clinic is not selected.',
          ),
        )
        return false
      }

      if (isSettingDefault) return false

      setIsSettingDefault(true)
      try {
        await setDefaultPaymentMethod(id, patientId, clinicId)
        await fetchPaymentMethods()
        showToast(
          createSuccessToast(
            'Default payment method updated',
            'Your default payment method has been updated.',
          ),
        )
        return true
      } catch (err: unknown) {
        showToast(
          createFailedToast(
            'Could not set default payment method',
            extractApiError(err, 'Please try again.'),
          ),
        )
        return false
      } finally {
        setIsSettingDefault(false)
      }
    },
    [
      fetchPaymentMethods,
      isSettingDefault,
      selectedClinic?.clinic_id,
      selectedPatient?.patient_id,
      showToast,
    ],
  )

  return {
    paymentMethods,
    savedCards,
    savedCardDisplays,
    isLoading,
    isDeleting,
    isSettingDefault,
    refetch: fetchPaymentMethods,
    deletePaymentMethodById,
    setDefaultPaymentMethodById,
  }
}

export default usePatientProfilePaymentMethods
